import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch, downloadBlob } from "../../api/fddClient";

/**
 * The S-corp report as a partner reads it: the conclusion and the few things
 * that matter first, everything else one click deeper. Tabs for each output,
 * every item a single line with its detail and sources behind "more".
 */

interface Source { file: string; page?: number; locator?: string; quote?: string }
/** Set when the READING of a box is in doubt: the two readings disagreed, or only one could read it. */
interface ReadingCheck { status: "contested" | "unconfirmed"; detail: string }
interface Fact { id: string; topic: string; label: string; value: string | number | boolean | null; raw?: string; date?: string; period?: string; source: Source; grade: string; method: string; check?: ReadingCheck }
interface Event { date: string | null; type: string; description: string; grade: string; sources: Source[] }
interface Exception { id: string; rule: string; severity: "contradicted" | "gap" | "review"; title: string; detail: string; facts: string[]; sources: Source[] }
interface Answer { id: string; text: string; status: string; answer: string; grade?: string; sources: Source[] }
interface RequestLine { id: string; trigger: string; text: string }
interface CoverageItem {
  id: string; title: string; required: boolean; status: "found" | "missing"; files: string[];
  /** Files the name suggested and the content ruled out. */
  rejected?: Array<{ file: string; reason: string }>;
  /** Files credited on their name alone. */
  unverified?: string[];
}
interface Evidence { factId: string; label: string; value: string; source: Source; grade: string; check?: ReadingCheck }
/** What became of one file offered to the review. */
interface DocumentState {
  file: string; proposed: string[]; confirmed: string[]; rejected: Array<{ item: string; reason: string }>;
  contents: string[]; read: "read" | "partly_read" | "failed" | "not_read"; note: string; facts: number;
}
interface RegisterRow {
  id: string; group: string; groupTitle: string; requirement: string;
  documents: Array<{ title: string; status: "found" | "missing" | "n/a" }>; returnFields: string[];
  have: "yes" | "partly" | "no" | "na"; missing: string[];
  verdict: "meets" | "does_not_meet" | "undetermined" | "review" | "na"; finding: string;
  evidence: Evidence[]; exceptionIds: string[]; followUps: string[]; resolution: string;
}

export interface ReportData {
  coverage: { items: CoverageItem[]; unmatched: string[] };
  facts: Fact[];
  timeline: Event[];
  exceptions: Exception[];
  answers: Answer[];
  requests: RequestLine[];
  questions: RequestLine[];
  register: RegisterRow[];
  /** Absent on a report saved before read states were recorded. */
  documents?: DocumentState[];
  warnings?: string[];
}

const READ: Record<DocumentState["read"], { label: string; bg: string; fg: string }> = {
  read: { label: "Read", bg: "#dcfce7", fg: "#166534" },
  partly_read: { label: "Partly read", bg: "#fef3c7", fg: "#92400e" },
  failed: { label: "Could not be read", bg: "#fee2e2", fg: "#991b1b" },
  not_read: { label: "Not read", bg: "#f3f4f6", fg: "#374151" },
};

type Tab = "register" | "summary" | "exceptions" | "timeline" | "answers" | "requests" | "documents" | "facts";
const TABS: Array<[Tab, string]> = [
  ["register", "Requirements"],
  ["summary", "Summary"],
  ["exceptions", "Exceptions"],
  ["timeline", "Timeline"],
  ["answers", "Questions answered"],
  ["requests", "Requests"],
  ["documents", "Documents"],
  ["facts", "Facts"],
];

const green = "var(--theme-ink)";
const line = "var(--theme-border-strong)";
const muted = "#5f6b76";
const SEV: Record<string, { label: string; bg: string; fg: string }> = {
  contradicted: { label: "Contradicted", bg: "#fee2e2", fg: "#991b1b" },
  gap: { label: "Gap", bg: "#fef3c7", fg: "#92400e" },
  review: { label: "Review", bg: "#e0f2fe", fg: "#075985" },
};
const GRADE: Record<string, { bg: string; fg: string }> = {
  proven: { bg: "#dcfce7", fg: "#166534" },
  supported: { bg: "#e0f2fe", fg: "#075985" },
  represented: { bg: "#fef3c7", fg: "#92400e" },
  contradicted: { bg: "#fee2e2", fg: "#991b1b" },
};
const STATUS: Record<string, { bg: string; fg: string }> = {
  answered: { bg: "#dcfce7", fg: "#166534" },
  partial: { bg: "#fef3c7", fg: "#92400e" },
  open: { bg: "#f3f4f6", fg: "#374151" },
  found: { bg: "#dcfce7", fg: "#166534" },
  missing: { bg: "#fee2e2", fg: "#991b1b" },
};

const VERDICT: Record<string, { label: string; bg: string; fg: string }> = {
  meets: { label: "Meets", bg: "#dcfce7", fg: "#166534" },
  does_not_meet: { label: "Does not meet", bg: "#fee2e2", fg: "#991b1b" },
  undetermined: { label: "Need information", bg: "#fef3c7", fg: "#92400e" },
  review: { label: "For review", bg: "#e0f2fe", fg: "#075985" },
  na: { label: "Not applicable", bg: "#f3f4f6", fg: "#374151" },
};
const HAVE: Record<string, { label: string; bg: string; fg: string }> = {
  yes: { label: "Yes", bg: "#dcfce7", fg: "#166534" },
  partly: { label: "Partly", bg: "#fef3c7", fg: "#92400e" },
  no: { label: "No", bg: "#fee2e2", fg: "#991b1b" },
  na: { label: "n/a", bg: "#f3f4f6", fg: "#374151" },
};

const Badge: React.FC<{ text: string; bg: string; fg: string }> = ({ text, bg, fg }) => (
  <span style={{ background: bg, color: fg, borderRadius: 999, padding: "2px 9px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, whiteSpace: "nowrap" }}>{text}</span>
);

/** A small marker on a value whose reading rests on one source; the reason is in its tooltip. */
const Doubt: React.FC<{ check?: ReadingCheck }> = ({ check }) =>
  check ? (
    <span title={check.detail} style={{ marginLeft: 6 }}>
      <Badge text={check.status === "contested" ? "readings disagree" : "one reading"} bg="#fef3c7" fg="#92400e" />
    </span>
  ) : null;

const money = (v: Fact["value"] | undefined): string =>
  typeof v === "number" ? `${v < 0 ? "-" : ""}$${Math.abs(Math.round(v)).toLocaleString("en-US")}` : v === null || v === undefined ? "–" : String(v);

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso ?? "undated";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}-${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][Number(m) - 1]}-${y.slice(2)}`;
};

const cite = (s: Source): string => `${s.file}${s.page ? `, p.${s.page}` : ""}${s.locator ? ` (${s.locator})` : ""}`;

const Sources: React.FC<{ sources: Source[] }> = ({ sources }) => {
  const uniq = [...new Map(sources.map((s) => [cite(s), s])).values()];
  if (uniq.length === 0) return null;
  return (
    <div style={{ fontSize: 12, color: muted, marginTop: 6 }}>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>Sources</div>
      {uniq.map((s) => (
        <div key={cite(s)}>
          {cite(s)}
          {s.quote ? <span style={{ fontStyle: "italic" }}> — “{s.quote.slice(0, 160)}{s.quote.length > 160 ? "…" : ""}”</span> : null}
        </div>
      ))}
    </div>
  );
};

/** One line, with the detail behind a toggle. */
const Row: React.FC<{ head: React.ReactNode; children?: React.ReactNode; open?: boolean }> = ({ head, children, open = false }) => {
  const [show, setShow] = useState(open);
  return (
    <div style={{ borderTop: `1px solid ${line}`, padding: "8px 4px" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <div style={{ flex: 1, fontSize: 14 }}>{head}</div>
        {children ? (
          <button onClick={() => setShow((s) => !s)} style={{ background: "#fff", color: green, border: `1px solid ${line}`, borderRadius: 6, padding: "2px 8px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
            {show ? "less" : "more"}
          </button>
        ) : null}
      </div>
      {show && children ? <div style={{ marginTop: 6, fontSize: 13, color: "#1b1f23" }}>{children}</div> : null}
    </div>
  );
};

const Card: React.FC<{ title: string; children: React.ReactNode; right?: React.ReactNode }> = ({ title, children, right }) => (
  <section style={{ border: `1px solid ${line}`, borderRadius: 10, background: "#fff", padding: "12px 14px" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
      <div style={{ fontWeight: 700, color: green, fontSize: 14 }}>{title}</div>
      {right}
    </div>
    {children}
  </section>
);

const CopyAll: React.FC<{ lines: string[]; label: string }> = ({ lines, label }) => {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={async () => {
        await navigator.clipboard.writeText(lines.join("\n"));
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
      style={{ background: "#fff", color: green, border: `1px solid ${line}`, borderRadius: 6, padding: "3px 10px", fontSize: 12, cursor: "pointer" }}
    >
      {done ? "Copied" : label}
    </button>
  );
};

type Props = {
  data: ReportData;
  title: string;
  jobId: string;
  projectId: string;
  /** The Word document fetched when the run finished; null if it could not be. */
  docx: Blob | null;
  onBack?: () => void;
};

const ScorpReport: React.FC<Props> = ({ data, title, jobId, projectId, docx, onBack }) => {
  const [tab, setTab] = useState<Tab>("register");
  const [downloadError, setDownloadError] = useState<string | null>(null);

  /** The Word document: the copy kept with the report, or fetched while the server still holds the run. */
  const downloadWord = async () => {
    setDownloadError(null);
    try {
      let blob = docx;
      if (!blob) {
        const res = await apiFetch(`/api/tax/scorp/jobs/${jobId}/docx?projectId=${encodeURIComponent(projectId)}`);
        if (!res.ok) throw new Error("The Word document is no longer available — run the review again to produce it.");
        blob = await res.blob();
      }
      downloadBlob(blob, `S-corp status - ${title.replace(/[^A-Za-z0-9 ,.&-]/g, "")}.docx`);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : String(err));
    }
  };

  /** The run's data files, built from the report this browser holds, so they outlive the server's copy. */
  const dataFiles: Array<[string, unknown]> = [
    ["facts.json", data.facts],
    ["timeline.json", data.timeline],
    ["exceptions.json", data.exceptions],
    ["answers.json", data.answers],
    ["requests.json", { requests: data.requests, questions: data.questions }],
    ...(data.documents?.length ? [["documents.json", data.documents] as [string, unknown]] : []),
  ];
  const downloadJson = (name: string, value: unknown) =>
    downloadBlob(new Blob([JSON.stringify(value, null, 2)], { type: "application/json" }), name);
  const { coverage, facts, timeline, exceptions, answers, requests, questions, register } = data;
  const documents = useMemo(() => (data.documents ?? []).filter((d) => d.proposed.length > 0), [data.documents]);
  const warnings = data.warnings ?? [];
  const notFullyRead = documents.filter((d) => d.read !== "read");

  const byId = useMemo(() => new Map(facts.map((f) => [f.id, f])), [facts]);
  const years = useMemo(() => [...new Set(facts.map((f) => f.id.match(/^return\.(\d{4})\./)?.[1]).filter((y): y is string => !!y))].sort(), [facts]);
  const latest = years[years.length - 1];
  const ret = (key: string, y = latest) => (y ? byId.get(`return.${y}.${key}`)?.value : undefined);

  const contradicted = exceptions.filter((e) => e.severity === "contradicted");
  const gaps = exceptions.filter((e) => e.severity === "gap");
  const reviews = exceptions.filter((e) => e.severity === "review");
  const found = (id: string) => coverage.items.find((i) => i.id === id)?.status === "found";
  const election = years.map((y) => byId.get(`return.${y}.s_election_effective_date`)).find((f) => f?.date);
  const grade = contradicted.length ? "contradicted" : found("form_2553") && found("irs_acceptance") ? "proven" : election ? "supported" : "represented";
  const gradeText = contradicted.length
    ? `Contradicted on ${contradicted.length} item${contradicted.length > 1 ? "s" : ""}`
    : grade === "proven" ? "Proven" : grade === "supported" ? "Supported by the returns" : "Represented only";
  const missingRequired = coverage.items.filter((i) => i.required && i.status === "missing");
  const docsRead = new Set(facts.map((f) => f.source.file)).size;

  const tabButton = (t: Tab, label: string) => {
    const n = t === "register" ? register.length : t === "exceptions" ? exceptions.length : t === "answers" ? answers.length : t === "requests" ? requests.length + questions.length : t === "timeline" ? timeline.length : t === "facts" ? facts.length : t === "documents" ? coverage.items.length : 0;
    const active = tab === t;
    return (
      <button key={t} onClick={() => setTab(t)} style={{ background: active ? "var(--theme-accent)" : "#fff", color: active ? "#fff" : green, border: `1px solid ${active ? "var(--theme-accent)" : line}`, borderRadius: 8, padding: "7px 12px", fontSize: 13, fontWeight: active ? 700 : 500, cursor: "pointer" }}>
        {label}{n ? <span style={{ opacity: 0.8 }}> · {n}</span> : null}
      </button>
    );
  };

  const exceptionRow = (e: Exception, open = false) => (
    <Row
      key={e.id}
      open={open}
      head={<span><Badge text={SEV[e.severity].label} bg={SEV[e.severity].bg} fg={SEV[e.severity].fg} /> <span style={{ marginLeft: 6 }}>{e.title}</span></span>}
    >
      <div>{e.detail}</div>
      <Sources sources={e.sources} />
    </Row>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        {TABS.map(([t, l]) => tabButton(t, l))}
        <span style={{ flex: 1 }} />
        <button onClick={downloadWord} style={{ color: "#fff", background: "var(--theme-accent)", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Word document</button>
        {dataFiles.map(([f, value]) => (
          <button key={f} onClick={() => downloadJson(f, value)} style={{ color: green, background: "none", border: "none", padding: 0, fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>{f}</button>
        ))}
        {onBack ? <button onClick={onBack} style={{ background: "#fff", color: green, border: `1px solid ${line}`, borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>Back to the documents</button> : null}
      </div>
      {downloadError && <div style={{ color: "#991b1b", fontSize: 12 }}>{downloadError}</div>}

      {tab === "register" && <RegisterTab rows={register} jobId={jobId} projectId={projectId} />}

      {tab === "summary" && (
        <>
          <Card title={`${title}: S corporation status`} right={<Badge text={gradeText} bg={GRADE[grade].bg} fg={GRADE[grade].fg} />}>
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>
              {election ? <>S election effective <strong>{fmtDate(election.date)}</strong> per box A on {years.length} return{years.length === 1 ? "" : "s"} ({years.join(", ")}). </> : <>No return with an election date was read. </>}
              Form 2553 {found("form_2553") ? "on file" : "not on file"}; IRS acceptance {found("irs_acceptance") ? "on file" : "not on file"}.
              {" "}{documents.length ? documents.length - notFullyRead.length : docsRead} documents read{notFullyRead.length ? <>, <strong>{notFullyRead.length} not fully read</strong></> : null}, {facts.length} facts, {exceptions.length} exceptions ({contradicted.length} contradicted, {gaps.length} gaps, {reviews.length} for review), {missingRequired.length} required documents missing.
            </div>
          </Card>

          {notFullyRead.length > 0 && (
            <Card title="Documents in the data room that were not fully read">
              <div style={{ fontSize: 12, color: muted, marginBottom: 4 }}>A finding that rests on one of these stays open until it is read. Details are in the Documents tab.</div>
              {notFullyRead.map((d) => (
                <div key={d.file} style={{ borderTop: `1px solid ${line}`, padding: "6px 4px", fontSize: 13 }}>
                  <Badge text={READ[d.read].label} bg={READ[d.read].bg} fg={READ[d.read].fg} /> <span style={{ marginLeft: 6 }}>{d.file}</span>
                  {d.note ? <div style={{ color: muted, fontSize: 12, marginTop: 2 }}>{d.note}</div> : null}
                </div>
              ))}
            </Card>
          )}

          {contradicted.length > 0 && (
            <Card title="What the sources disagree on">
              {contradicted.map((e) => exceptionRow(e))}
            </Card>
          )}
          {gaps.length > 0 && (
            <Card title="What the data room does not establish">
              {gaps.map((e) => exceptionRow(e))}
            </Card>
          )}

          {latest && (
            <Card title={`Attributes at 31-Dec-${latest}`}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8, fontSize: 13 }}>
                {[
                  ["Ordinary business income", "ordinary_business_income"],
                  ["Officer compensation", "officer_compensation"],
                  ["Distributions", "k_distributions"],
                  ["AAA closing", "m2_aaa_end"],
                  ["OAA closing", "m2_oaa_end"],
                  ["Accumulated E&P", "m2_ep_end"],
                  ["Treasury stock", "l26_end"],
                  ["Total assets", "total_assets"],
                ].map(([label, key]) => (
                  <div key={key} style={{ border: `1px solid ${line}`, borderRadius: 8, padding: "6px 10px" }}>
                    <div style={{ color: muted, fontSize: 11 }}>{label}</div>
                    <div style={{ fontWeight: 700 }}>{money(ret(key))}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {reviews.length > 0 && (
            <Card title={`${reviews.length} items for review`}>
              {reviews.map((e) => exceptionRow(e))}
            </Card>
          )}
          <Card title="Next steps">
            <div style={{ fontSize: 13 }}>{requests.length} requests to send and {questions.length} questions for management are in the Requests tab, ready to paste.</div>
          </Card>
        </>
      )}

      {tab === "exceptions" && (
        <Card title="Every exception, by severity">
          {[...contradicted, ...gaps, ...reviews].map((e) => exceptionRow(e))}
        </Card>
      )}

      {tab === "timeline" && <TimelineTab events={timeline} />}

      {tab === "answers" && (
        <Card title="The question set">
          {answers.map((a) => (
            <Row
              key={a.id}
              head={
                <span>
                  <Badge text={a.status} bg={STATUS[a.status]?.bg ?? "#f3f4f6"} fg={STATUS[a.status]?.fg ?? "#374151"} />
                  {a.grade ? <span style={{ marginLeft: 4 }}><Badge text={a.grade} bg={GRADE[a.grade]?.bg ?? "#f3f4f6"} fg={GRADE[a.grade]?.fg ?? "#374151"} /></span> : null}
                  <span style={{ marginLeft: 8, fontWeight: 600 }}>{a.text}</span>
                </span>
              }
            >
              <div>{a.answer}</div>
              <Sources sources={a.sources} />
            </Row>
          ))}
        </Card>
      )}

      {tab === "requests" && (
        <>
          <Card title="Requests to send" right={<CopyAll lines={requests.map((r) => r.text)} label="Copy all requests" />}>
            {requests.map((r, i) => <div key={r.id} style={{ borderTop: `1px solid ${line}`, padding: "6px 4px", fontSize: 14 }}>{i + 1}. {r.text}</div>)}
          </Card>
          <Card title="Questions for management" right={<CopyAll lines={questions.map((q) => q.text)} label="Copy all questions" />}>
            {questions.map((q, i) => <div key={q.id} style={{ borderTop: `1px solid ${line}`, padding: "6px 4px", fontSize: 14 }}>{i + 1}. {q.text}</div>)}
          </Card>
        </>
      )}

      {tab === "documents" && (
        <>
          <Card title="Documents an S-corp review needs">
            {coverage.items.map((i) => (
              <Row
                key={i.id}
                head={
                  <span>
                    <Badge text={i.status} bg={STATUS[i.status].bg} fg={STATUS[i.status].fg} /> <span style={{ marginLeft: 6 }}>{i.title}{i.required ? "" : <span style={{ color: muted }}> (optional)</span>}</span>
                    {i.rejected?.length ? <span style={{ color: "#92400e", fontSize: 12, marginLeft: 8 }}>{i.rejected.length} file{i.rejected.length > 1 ? "s" : ""} ruled out by content</span> : null}
                  </span>
                }
              >
                {i.files.length ? (
                  i.files.map((f) => (
                    <div key={f}>
                      {f}
                      {i.unverified?.includes(f) ? <span style={{ color: muted, fontSize: 12 }}> — credited on its name; its content was not checked</span> : null}
                    </div>
                  ))
                ) : (
                  <div style={{ color: muted }}>{i.rejected?.length ? "No file in the data room is this document." : "Not in the folders searched."}</div>
                )}
                {(i.rejected ?? []).map((r) => (
                  <div key={r.file} style={{ color: "#92400e", fontSize: 12, marginTop: 4 }}>Not credited: {r.file} — {r.reason}</div>
                ))}
              </Row>
            ))}
            {coverage.unmatched.length > 0 && (
              <div style={{ fontSize: 12, color: muted, marginTop: 8 }}>Searched but not matched to any document type: {coverage.unmatched.join("; ")}</div>
            )}
          </Card>

          {documents.length > 0 && (
            <Card title="What each file held, and whether it was read">
              {documents.map((d) => (
                <Row key={d.file} head={<span><Badge text={READ[d.read].label} bg={READ[d.read].bg} fg={READ[d.read].fg} /> <span style={{ marginLeft: 6 }}>{d.file}</span>{d.note ? <span style={{ color: muted, fontSize: 12, marginLeft: 8 }}>{d.note}</span> : null}</span>}>
                  <div>{d.contents.length ? d.contents.join("; ") : <span style={{ color: muted }}>Its contents could not be listed from its text.</span>}</div>
                  {d.read !== "not_read" ? <div style={{ color: muted, fontSize: 12, marginTop: 4 }}>{d.facts} fact{d.facts === 1 ? "" : "s"} read from it.</div> : null}
                </Row>
              ))}
            </Card>
          )}

          {warnings.length > 0 && (
            <Card title="Reading notes">
              {warnings.map((w, i) => <div key={i} style={{ borderTop: `1px solid ${line}`, padding: "4px", fontSize: 12, color: muted }}>{w}</div>)}
            </Card>
          )}
        </>
      )}

      {tab === "facts" && <FactsTab facts={facts} />}
    </div>
  );
};

/**
 * A source snip, fetched with the signed-in token: an <img src> cannot carry
 * it. The server keeps a run's documents for two idle hours, so a report
 * reopened later from this browser gets a 404 here and the viewer falls back
 * to the quoted record.
 */
const SnipImage: React.FC<{ jobId: string; projectId: string; factId: string; alt: string; onFail: () => void }> = ({ jobId, projectId, factId, alt, onFail }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    let objectUrl: string | null = null;
    apiFetch(`/api/tax/scorp/jobs/${jobId}/snip/${encodeURIComponent(factId)}?projectId=${encodeURIComponent(projectId)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        objectUrl = URL.createObjectURL(await res.blob());
        if (live) setUrl(objectUrl);
      })
      .catch(() => {
        if (live) onFail();
      });
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [jobId, projectId, factId, onFail]);
  if (!url) return <div style={{ color: muted, fontSize: 13, padding: 8 }}>Loading the page…</div>;
  return <img src={url} alt={alt} style={{ width: "100%", height: "auto", border: `1px solid ${line}`, background: "#fff" }} />;
};

/** One source snip at a time, with previous and next. */
const ProofViewer: React.FC<{ evidence: Evidence[]; start: number; jobId: string; projectId: string; onClose: () => void }> = ({ evidence, start, jobId, projectId, onClose }) => {
  const [i, setI] = useState(start);
  const [failed, setFailed] = useState<Set<string>>(new Set());
  const e = evidence[i];
  const hasImage = /\.pdf$/i.test(e.source.file) && !!e.source.page && !failed.has(e.factId);
  const nav = (d: number) => setI((k) => Math.min(evidence.length - 1, Math.max(0, k + d)));
  const factId = e.factId;
  const markFailed = useCallback(() => setFailed((p) => new Set(p).add(factId)), [factId]);
  const navBtn = (primary: boolean): React.CSSProperties => ({ background: primary ? "var(--theme-accent)" : "#fff", color: primary ? "#fff" : green, border: `1px solid ${primary ? "var(--theme-accent)" : line}`, borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: 13 });
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(ev) => ev.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "min(1100px, 100%)", maxHeight: "92vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: `1px solid ${line}` }}>
          <strong style={{ color: green, flex: 1, fontSize: 14 }}>Proof {i + 1} of {evidence.length}</strong>
          <button onClick={() => nav(-1)} disabled={i === 0} style={navBtn(false)}>Previous</button>
          <button onClick={() => nav(1)} disabled={i === evidence.length - 1} style={navBtn(true)}>Next</button>
          <button onClick={onClose} style={{ ...navBtn(false), color: muted }}>Close</button>
        </div>
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${line}`, fontSize: 13 }}>
          <div><strong>{e.label}</strong>{e.value ? <span>: {e.value}</span> : null} <Badge text={e.grade} bg={GRADE[e.grade]?.bg ?? "#f3f4f6"} fg={GRADE[e.grade]?.fg ?? "#374151"} /><Doubt check={e.check} /></div>
          <div style={{ color: muted, marginTop: 2 }}>{cite(e.source)}</div>
          {e.check ? <div style={{ color: "#92400e", marginTop: 4 }}>The reading of this box is in doubt: {e.check.detail}. Check it on the page below.</div> : null}
        </div>
        <div style={{ overflow: "auto", padding: 14, background: "#f6f8f7", flex: 1 }}>
          {hasImage ? (
            <SnipImage key={e.factId} jobId={jobId} projectId={projectId} factId={e.factId} alt={e.label} onFail={markFailed} />
          ) : (
            <div style={{ background: "#fff", border: `1px solid ${line}`, borderRadius: 8, padding: 16, fontSize: 14 }}>
              <div style={{ color: muted, fontSize: 12, marginBottom: 6 }}>
                No page image for this source, or the run has been cleared from the server (it keeps a run's documents for two idle hours). The record as read:
              </div>
              {e.source.quote ? <blockquote style={{ margin: 0, paddingLeft: 12, borderLeft: `3px solid ${green}` }}>{e.source.quote}</blockquote> : <div>{e.value}</div>}
              {e.source.locator ? <div style={{ color: muted, fontSize: 12, marginTop: 8 }}>Location: {e.source.locator}</div> : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** The requirements register: the table a partner reads first. */
const RegisterTab: React.FC<{ rows: RegisterRow[]; jobId: string; projectId: string }> = ({ rows, jobId, projectId }) => {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [proof, setProof] = useState<{ row: RegisterRow; start: number } | null>(null);
  const toggle = (id: string) => setOpen((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const counts: Record<string, number> = {};
  for (const r of rows) counts[r.verdict] = (counts[r.verdict] ?? 0) + 1;
  const groups = [...new Set(rows.map((r) => r.group))];
  const th: React.CSSProperties = { textAlign: "left", padding: "8px 8px", fontSize: 12, color: muted, textTransform: "uppercase", letterSpacing: 0.3, borderBottom: `1px solid ${line}` };
  const td: React.CSSProperties = { padding: "10px 8px", verticalAlign: "top", borderTop: `1px solid ${line}`, fontSize: 13 };
  const small: React.CSSProperties = { background: "var(--theme-accent)", color: "#fff", border: 0, borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 12, marginBottom: 6 };
  return (
    <>
      <Card title="Requirements to be an S corporation" right={
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {Object.entries(VERDICT).filter(([k]) => counts[k]).map(([k, v]) => <Badge key={k} text={`${v.label} ${counts[k]}`} bg={v.bg} fg={v.fg} />)}
        </div>
      }>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
            <thead><tr><th style={{ ...th, width: "32%" }}>Requirement</th><th style={{ ...th, width: "22%" }}>Relevant documents</th><th style={{ ...th, width: "12%" }}>Have what we need</th><th style={{ ...th, width: "34%" }}>Finding so far</th></tr></thead>
            <tbody>
              {groups.map((g) => (
                <React.Fragment key={g}>
                  <tr><td colSpan={4} style={{ padding: "12px 8px 4px", fontWeight: 700, color: green, fontSize: 13 }}>{rows.find((r) => r.group === g)?.groupTitle}</td></tr>
                  {rows.filter((r) => r.group === g).map((r) => (
                    <React.Fragment key={r.id}>
                      <tr onClick={() => toggle(r.id)} style={{ cursor: "pointer", background: open.has(r.id) ? "var(--theme-tint-7)" : undefined }}>
                        <td style={td}><span style={{ color: muted, marginRight: 6 }}>{r.id}</span>{r.requirement}</td>
                        <td style={td}>
                          {r.documents.map((d) => <div key={d.title} style={{ display: "flex", gap: 6, alignItems: "baseline", marginBottom: 2 }}><span style={{ width: 7, height: 7, borderRadius: 7, flexShrink: 0, background: d.status === "found" ? "#16a34a" : d.status === "missing" ? "#dc2626" : "#9ca3af" }} /><span style={{ fontSize: 12 }}>{d.title}</span></div>)}
                          {r.returnFields.length ? <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>Return: {r.returnFields.join("; ")}</div> : null}
                        </td>
                        <td style={td}><Badge text={HAVE[r.have].label} bg={HAVE[r.have].bg} fg={HAVE[r.have].fg} />{r.missing.length ? <div style={{ fontSize: 11, color: muted, marginTop: 4 }}>{r.missing.length} follow-up{r.missing.length > 1 ? "s" : ""}</div> : null}</td>
                        <td style={td}><Badge text={VERDICT[r.verdict].label} bg={VERDICT[r.verdict].bg} fg={VERDICT[r.verdict].fg} /><div style={{ marginTop: 4 }}>{r.finding}</div></td>
                      </tr>
                      {open.has(r.id) && (
                        <tr><td colSpan={4} style={{ padding: "4px 8px 14px 8px", background: "var(--theme-tint-4)" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14, fontSize: 13 }}>
                            <div>
                              <div style={{ fontWeight: 700, color: green, marginBottom: 4 }}>Evidence ({r.evidence.length})</div>
                              {r.evidence.length === 0 ? <div style={{ color: muted }}>Nothing read for this requirement yet.</div> : (
                                <>
                                  <button onClick={() => setProof({ row: r, start: 0 })} style={small}>Review the proof</button>
                                  <div style={{ maxHeight: 180, overflow: "auto" }}>
                                    {r.evidence.map((e, i) => <div key={e.factId} onClick={() => setProof({ row: r, start: i })} style={{ cursor: "pointer", padding: "2px 0", borderTop: `1px solid ${line}` }}>{e.label}{e.value ? `: ${e.value}` : ""} <span style={{ color: muted, fontSize: 11 }}>({e.source.file}{e.source.page ? ` p.${e.source.page}` : ""})</span><Doubt check={e.check} /></div>)}
                                  </div>
                                </>
                              )}
                            </div>
                            <div>
                              {r.missing.length > 0 && (<><div style={{ fontWeight: 700, color: "#92400e", marginBottom: 4 }}>Still needed</div><ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>{r.missing.map((m) => <li key={m}>{m}</li>)}</ul></>)}
                              {r.followUps.length > 0 && (<><div style={{ fontWeight: 700, color: green, marginBottom: 4 }}>Follow-ups</div><ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>{r.followUps.map((f) => <li key={f}>{f}</li>)}</ul></>)}
                              <div style={{ fontWeight: 700, color: green, marginBottom: 4 }}>If it fails</div>
                              <div>{r.resolution}</div>
                            </div>
                          </div>
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {proof && <ProofViewer evidence={proof.row.evidence} start={proof.start} jobId={jobId} projectId={projectId} onClose={() => setProof(null)} />}
    </>
  );
};

const TimelineTab: React.FC<{ events: Event[] }> = ({ events }) => {
  const types = [...new Set(events.map((e) => e.type))];
  const [on, setOn] = useState<Set<string>>(new Set(types.filter((t) => t !== "statement" && t !== "distribution_year")));
  const shown = events.filter((e) => on.has(e.type));
  return (
    <Card title="The corporation's life, dated and sourced" right={
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {types.map((t) => (
          <button key={t} onClick={() => setOn((p) => { const n = new Set(p); n.has(t) ? n.delete(t) : n.add(t); return n; })} style={{ background: on.has(t) ? "var(--theme-accent)" : "#fff", color: on.has(t) ? "#fff" : green, border: `1px solid ${line}`, borderRadius: 999, padding: "2px 9px", fontSize: 11, cursor: "pointer" }}>
            {t.replace(/_/g, " ")}
          </button>
        ))}
      </div>
    }>
      {shown.map((e, i) => (
        <Row key={i} head={<span><span style={{ display: "inline-block", width: 86, color: muted, fontVariantNumeric: "tabular-nums" }}>{fmtDate(e.date)}</span><Badge text={e.grade} bg={GRADE[e.grade]?.bg ?? "#f3f4f6"} fg={GRADE[e.grade]?.fg ?? "#374151"} /> <span style={{ marginLeft: 6 }}>{e.description}</span></span>}>
          <Sources sources={e.sources} />
        </Row>
      ))}
    </Card>
  );
};

const FactsTab: React.FC<{ facts: Fact[] }> = ({ facts }) => {
  const [q, setQ] = useState("");
  const [topic, setTopic] = useState("");
  const [limit, setLimit] = useState(200);
  const topics = [...new Set(facts.map((f) => f.topic))].sort();
  const needle = q.trim().toLowerCase();
  const rows = facts.filter((f) => (!topic || f.topic === topic) && (!needle || `${f.id} ${f.label} ${String(f.value)} ${f.source.file}`.toLowerCase().includes(needle)));
  return (
    <Card title="Every fact, with its source" right={
      <div style={{ display: "flex", gap: 8 }}>
        <select value={topic} onChange={(e) => setTopic(e.target.value)} style={{ border: `1px solid ${line}`, borderRadius: 6, padding: "3px 6px", fontSize: 12 }}>
          <option value="">all topics</option>
          {topics.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search" style={{ border: `1px solid ${line}`, borderRadius: 6, padding: "3px 8px", fontSize: 12 }} />
      </div>
    }>
      <div style={{ fontSize: 12, color: muted, marginBottom: 4 }}>{rows.length} of {facts.length}</div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead><tr style={{ textAlign: "left", color: muted }}><th style={{ padding: 4 }}>Fact</th><th style={{ padding: 4 }}>Value</th><th style={{ padding: 4 }}>Period</th><th style={{ padding: 4 }}>Grade</th><th style={{ padding: 4 }}>Source</th></tr></thead>
        <tbody>
          {rows.slice(0, limit).map((f) => (
            <tr key={f.id} style={{ borderTop: `1px solid ${line}` }}>
              <td style={{ padding: 4 }} title={f.id}>{f.label}</td>
              <td style={{ padding: 4, whiteSpace: "nowrap" }}>{typeof f.value === "number" ? money(f.value) : String(f.value ?? "–").slice(0, 80)}<Doubt check={f.check} /></td>
              <td style={{ padding: 4 }}>{f.period ?? ""}</td>
              <td style={{ padding: 4 }}><Badge text={f.grade} bg={GRADE[f.grade]?.bg ?? "#f3f4f6"} fg={GRADE[f.grade]?.fg ?? "#374151"} /></td>
              <td style={{ padding: 4, color: muted }}>{cite(f.source)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > limit && <button onClick={() => setLimit((l) => l + 200)} style={{ marginTop: 8, background: "#fff", color: green, border: `1px solid ${line}`, borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>Show more</button>}
    </Card>
  );
};

export default ScorpReport;
